import { Button } from "@community-project-org/ui";
import { useQuery } from "@tanstack/react-query";
import { QrCode as QrIcon } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { apiRequest } from "../../common/api/apiClient.js";
import {
  BookingStatusPill,
  type BookingStatus,
} from "../../common/components/BookingStatusPill.js";
import { formatWhen } from "../../common/components/formatWhen.js";
import { Money } from "../../common/components/Money.js";
import { QrCode } from "../../common/components/QrCode.js";
import { MemberPasses } from "../members/MemberPasses.js";
import {
  ServiceTypeBadge,
  serviceTypeClass,
} from "../../common/components/ServiceTypeBadge.js";
import { formatBookingCode } from "../bookingCode.js";
import type { components } from "../../generated/api.js";

type BookingDTO = components["schemas"]["BookingDTO"];

/**
 * Die eigenen Tickets und Mitgliedsausweise. Ausweise stehen oben, weil man sie am
 * Eingang sucht; der QR-Code eines Tickets öffnet sich per Tipp, damit die Liste
 * überschaubar bleibt.
 */
export function MyBookingsPage(): JSX.Element {
  const bookings = useQuery({
    queryKey: ["my-bookings"],
    queryFn: () => apiRequest<BookingDTO[]>("/my/bookings", { tenant: null }),
  });

  const [open, setOpen] = useState<string | null>(null);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-6">
      <h1 className="pt-4 text-[22px] font-extrabold tracking-[-0.03em] text-ink">Tickets</h1>

      <MemberPasses />

      {bookings.isPending && <p className="mt-6 text-[14px] text-ink-3">Wird geladen …</p>}

      {bookings.data?.length === 0 && (
        <p className="mt-6 text-[14px] text-ink-3">
          Noch keine Buchung. Was du buchst, liegt danach hier — mit QR-Code.
        </p>
      )}

      <ul className="mt-5 flex flex-col gap-2.5">
        {bookings.data?.map((booking) => (
          <li
            key={booking.id}
            className={`${serviceTypeClass(booking.serviceType)} card pad`}
          >
            <div className="flex items-start gap-3">
              <ServiceTypeBadge type={booking.serviceType} square />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-bold tracking-[-0.015em] text-ink">
                  {booking.serviceTitle}
                </p>
                {booking.startsAt && (
                  <p className="num mt-0.5 text-[12.5px] font-bold text-ink-2">
                    {formatWhen(booking.startsAt, booking.venueTimeZone)}
                  </p>
                )}
                {booking.route && <p className="mt-0.5 text-[12.5px] text-ink-2">{booking.route}</p>}
                {/* Der Code auch im Klartext — Rückfall, wenn ein Scanner streikt. */}
                <p className="num mt-1 text-[11px] font-bold text-ink-3">
                  {formatBookingCode(booking.code)}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <BookingStatusPill status={booking.status as BookingStatus} />
                <span className="text-[14px] font-extrabold text-ink">
                  <Money cents={booking.amountCents} currency={booking.currency} />
                </span>
                {booking.discountCents > 0 && (
                  <span className="text-[11.5px] font-bold text-ink-3">Mitgliedsvorteil</span>
                )}
              </div>
            </div>
            {(booking.status === "CONFIRMED" || booking.status === "CREATED") && (
              <div className="mt-3 grid justify-items-center gap-2">
                <Button
                  variant="secondary"
                  aria-expanded={open === booking.id}
                  onClick={() => setOpen(open === booking.id ? null : booking.id)}
                >
                  <QrIcon size={16} aria-hidden="true" />
                  {open === booking.id ? "QR-Code ausblenden" : "QR-Code zeigen"}
                </Button>
                {open === booking.id && <QrCode value={booking.code} label={`Ticket ${booking.serviceTitle}`} />}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
